// Avatar-row presence widget. Renamed from CalOnlineUsersBar /
// CalOnlineAvatar (2026-05-14) — it isn't calendar-specific; it shows
// every signed-in member of the current org that the SDK presence
// channel reports as online.
//
// Source of truth: `root.onlineUsers` is the synced list maintained by
// `useOnlinePresence` (shared/functions/presence.js), subscribed at
// shell scope in workspace/app.js so the widget works on every route.
//
// Scoping: the presence channel itself is org-agnostic
// (`scope: 'symbols'`), so we intersect with `root.people` (the active
// org's roster, loaded by workspace-project's loadPrivateData). Only
// emails that exist in peopleByEmail render — strangers from other
// orgs and unverified emails are dropped.
//
// Render contract:
//   - Self is filtered out (you don't need to see your own dot).
//   - Up to 5 avatars render inline.
//   - When more than 5 are online, the 5th slot becomes a "+N" chip
//     that opens a dropdown listing the rest.
//   - When the visible list is empty, the entire widget hides — no
//     vacant chrome / phantom padding next to Messenger.

// Avatars use the shared built-in Avatar (DiceBear image via
// el.call('avatarUrlFor', email)) — no local initials helper needed.

// Pure projection so OnlineMembers (avatar list) + OnlineMembersOverflow
// (dropdown list) share the same source-of-truth filter. Order: alpha
// by display name so the avatar strip is stable across syncs.
// Memoized on (onlineUsers, people, me) identity. This projection is read from
// ~6 factories per render (if / Avatars.children / OverflowChip if+title+text /
// Dropdown.children); onlineUsers + people are stable within a render tick, so
// without a cache the reduce+filter+sort ran 6× per presence sync. Now it runs
// once and the 6 reads share the result.
let _vmCache = null
const _visibleMembers = (root) => {
  const online = root?.onlineUsers || []
  const people = root?.people || []
  const me = root?.currentUserEmail
  if (_vmCache && _vmCache.online === online && _vmCache.people === people && _vmCache.me === me) {
    return _vmCache.result
  }
  // Emails are matched CASE-INSENSITIVELY on both sides: presence keys come
  // from the auth email while the roster rows come from invites/imports, and
  // a single case mismatch silently dropped a genuinely-online member from
  // the bar ("doesn't show all online users").
  const meLc = String(me || '').toLowerCase()
  const byEmail = people.reduce((acc, p) => {
    if (p?.email) acc[String(p.email).toLowerCase()] = p
    return acc
  }, {})
  const result = online
    .filter((u) => {
      const email = String(u?.email || '').toLowerCase()
      return email && email !== meLc && byEmail[email]
    })
    .map((u) => {
      const person = byEmail[String(u.email).toLowerCase()]
      return {
        email: u.email,
        name: person?.name || u.email,
        avatar: person?.avatar || null
      }
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  _vmCache = { online, people, me, result }
  return result
}

const MAX_INLINE = 5

export const OnlineMember = {
  extends: 'Avatar',
  position: 'relative',
  // B1 + the 2px ring each side lands the avatar's OUTER height just under
  // the CanvasPromptTextarea's ~39px pill, so the bottom-left dock reads as
  // one line with the prompt bar. B2 measured ~40px WITH the ring — still
  // taller than the pill (2026-07-29 per Nika "online avatars are bigger in
  // height than CanvasPromptArea navbar"). Was C → B2 → B1.
  boxSize: 'B1',
  round: '50%',
  // KEPT — avatar-stack ring (docks the presence chip so it reads as a
  // distinct circle). Retokenized `'2px'` → `hairline2`.
  borderWidth: 'hairline2',
  borderStyle: 'solid',
  flexShrink: '0',
  marginLeft: '-Z',
  theme: 'dialog',
  align: 'center center',
  cursor: 'pointer',
  title: (el, s) => s.name || s.email || '',
  src: (el, s) => el.call('avatarUrlFor', s.email || ''),
  // Anchored mini-profile + quick actions (Chat/Call/Book a meeting/Profile/
  // Assign Ticket/Ping) — replaces the old direct-to-modal openProfileModal
  // jump (2026-07-30 per Nika "on bottom left avatars click - open popover").
  // The popover's own Profile button is what opens the full profile page now.
  onClick: (e, el, s) => {
    if (s.email) el.call('openPersonPopover', s.email, el)
  },

  OnlineDot: {
    position: 'absolute',
    bottom: '-1px',
    right: '-1px',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'green',
    // KEPT — punched-out presence-dot ring. Retokenized `'2px'` → `hairline2`.
    borderWidth: 'hairline2',
    borderStyle: 'solid',
    theme: 'dialog'
  }
}

export const OnlineMembers = {
  // Hide the entire widget when no one else is online (after self-filter +
  // org-roster intersect). `hide:` (not `if:`) — v3.14's `if` is a
  // mount-time gate that never re-creates the element when presence later
  // syncs someone online, which left the dock permanently empty for a user
  // who booted alone. `hide` keeps the element mounted and toggles
  // reactively with the dock's onlineUsers subscription.
  hide: (el, s) => _visibleMembers(s.root).length === 0,

  position: 'relative',
  flow: 'x',
  align: 'center right',
  paddingLeft: 'Z',

  // Inline avatar strip. When length > MAX_INLINE we reserve the last
  // slot for the +N overflow chip; otherwise show all up to MAX_INLINE.
  Avatars: {
    flow: 'x',
    align: 'center right',
    childExtends: 'OnlineMember',
    childrenAs: 'state',
    children: (el, s) => {
      const list = _visibleMembers(s.root)
      const cap = list.length > MAX_INLINE ? MAX_INLINE - 1 : MAX_INLINE
      return list.slice(0, cap)
    }
  },

  OverflowChip: {
    // `hide:` for the same reason as the widget root — `if` is mount-time
    // only and would never reveal the chip when the 6th member comes online.
    hide: (el, s) => _visibleMembers(s.root).length <= MAX_INLINE,
    tag: 'button',
    type: 'button',
    position: 'relative',
    // Tracks OnlineMember.boxSize — the chip is the strip's fifth slot.
    width: 'B1',
    height: 'B1',
    round: '50%',
    marginLeft: '-Z',
    // KEPT — same avatar-stack ring as OnlineMember. Retokenized `'2px'` →
    // `hairline2`.
    border: 'hairline2 solid',
    theme: 'dialog',
    // Was a fixed dark 'gray3' + 'white' pair — always rendered as a
    // dark-mode chip even under `theme: 'dialog'`'s light-mode branch
    // (workspace/shared designSystem/theme.js: dialog.@light already gives
    // background:'gray16', color:'title'). 'overlayStrong' is the solid-
    // looking adaptive equivalent of that fixed dark chip (matches
    // ButtonTitle.js's `:hover > div` precedent) — 'overlay' (the mapping
    // table's literal gray3→overlay entry) is a 5%-opacity wash, too faint
    // to read as a bounded avatar-stack chip.
    background: 'overlayStrong',
    color: 'title',
    fontSize: 'Y2',
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: '0',
    align: 'center center',
    flow: 'x',
    title: (el, s) =>
      el
        .call('polyglot', 'workspace.onlineMembers.more')
        .replace('{count}', _visibleMembers(s.root).length - (MAX_INLINE - 1)),
    // `overlayStrong` is a sheet wash, not a state fill — banned as a hover
    // value outright (§4.3/§10), tone-shifted or not. A translucent `hover`
    // wash also wouldn't read stacked over this already-near-opaque fill
    // (§4.3 "washes don't read" case) — `filter: brightness()` is the
    // documented portable hover form for solid/imagery surfaces (Rule 65),
    // same precedent as MarketplaceDetailGallery's thumbnail hover.
    ':hover': { filter: 'brightness(1.12)' },
    text: (el, s) => `+${_visibleMembers(s.root).length - (MAX_INLINE - 1)}`,
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root.update({ _onlineMembersOpen: !s.root._onlineMembersOpen })
    }
  },

  Dropdown: {
    display: (el, s) => (s.root?._onlineMembersOpen ? 'flex' : 'none'),
    position: 'absolute',
    top: '120%',
    right: '0',
    zIndex: '1200',
    minWidth: 'G',
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: 'V',
    round: 'radiusControl',
    // Fill only — matches the house `MenuPanel` primitive (`theme` +
    // `shadow: 'overlay'` + no border); `dialog-elevated` is already a
    // near-opaque themed fill, so the hairline was redundant.
    theme: 'dialog-elevated',
    shadow: 'overlay',
    flow: 'y',
    gap: 'V',

    // Outside-press dismissal — declared, so DOMQL owns the registration and
    // the teardown (FA503). Was an `onCreate` add + `onRemove` remove pair
    // around an `el._close` stamp.
    onDocumentMousedown: (ev, el, s) => {
      if (!el.node || !el.node.parentElement) return
      if (el.node.parentElement.contains(ev.target)) return
      s.root.update({ _onlineMembersOpen: false })
    },

    Row: {
      childExtends: {
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        padding: 'V Z',
        round: 'radiusControl',
        cursor: 'pointer',
        ':hover': { background: 'hover' },
        onClick: (e, el, s) => {
          if (s.email) {
            // Close the overflow list FIRST — openPersonPopover snapshots the
            // clicked row's anchor rect synchronously, so this ordering (vs.
            // opening then closing) can't race the row's own unmount.
            el.state.root.update({ _onlineMembersOpen: false })
            el.call('openPersonPopover', s.email, el)
          }
        },

        Avatar: {
          boxSize: 'C',
          round: '50%',
          flexShrink: '0',
          align: 'center center',
          src: (el, s) => el.call('avatarUrlFor', s.email || '')
        },

        Name: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'title',
          flex: '1',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.name || s.email || ''
        }
      },
      childrenAs: 'state',
      // Show only the overflow members in the dropdown — the first
      // MAX_INLINE - 1 are already visible as inline avatars.
      children: (el, s) => _visibleMembers(s.root).slice(MAX_INLINE - 1)
    }
  }
}

// Fixed bottom-left presence dock — the widget's home since 2026-07-25 (per
// Nika "move this to bottom left corner"; it previously sat in the AppHeader
// right cluster). Mounted once at the shell root (workspace/app.js) so it
// shows on every route. The overflow dropdown opens UPWARD from here.
export const OnlineMembersDock = {
  position: 'fixed',
  left: 'A',
  // Same bottom inset as the CanvasPromptTextarea composer (its fixed
  // `bottom: 'X2'`), so the dock and the prompt bar share one baseline
  // (2026-07-29 per Nika "position it at bottom same as prompt navbar").
  // Was 'A' (16px), which floated the dock ~10px above the pill.
  bottom: 'X2',
  zIndex: '1150',
  flow: 'x',
  align: 'center left',

  // The dock is otherwise inert (nothing else re-renders it), so the inner
  // OnlineMembers `if:` — false at boot when nobody else is online — would
  // never re-evaluate when presence syncs arrive. In the old AppHeader
  // placement the header's constant re-renders masked this; here the
  // subscription IS the re-render source.
  subscribeTo: ['onlineUsers', 'people', '_onlineMembersOpen', 'workspaceProject'],

  // Same gates the header placement had: signed-in, active member, not an
  // auth route (the shell hides chrome there), plus OnlineMembers' own
  // nobody-else-online self-hide.
  hide: (el, s) => {
    const root = s?.root
    if (!root?.signedIn || !root?.currentUserEmail) return true
    // Immersive module chrome hides the presence dock with the rest of the
    // shell chrome (manifest chrome.immersive; admins keep it). Reactive via
    // subscribeTo 'workspaceProject' above. el.call — this file is a pure
    // DOMQL component (no imports); the fn is registered in functions/index.js.
    const immersiveFn =
      el?.context?.methods?.moduleImmersiveActive || el?.context?.functions?.moduleImmersiveActive
    if (typeof immersiveFn === 'function' && immersiveFn(root)) return true
    const isActiveFn = el?.context?.methods?.isActiveMember
    if (typeof isActiveFn === 'function' && !isActiveFn.call(el, s)) return true
    const path = root?.currentPath || '/'
    return /^\/(login|register|signup|auth|reset|forgot|_session-bridge)/.test(path)
  },
  '@mobileL': { display: 'none' },

  OnlineMembers: {
    paddingLeft: '0',
    // Open the overflow list UPWARD — the dock sits at the viewport bottom.
    Dropdown: {
      top: 'auto',
      bottom: '120%',
      right: 'auto',
      left: '0'
    }
  }
}

// Backwards-compat aliases — old name still used in AppHeader.js,
// calendar/Main.js, and migration-period comments. New code references
// OnlineMembers / OnlineMember.
export const CalOnlineUsersBar = OnlineMembers
export const CalOnlineAvatar = OnlineMember
